import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import '../../utils/assertion.esm.js';
import '../../utils/ssr.esm.js';
import '../../utils/number.esm.js';
import '../../utils/array.esm.js';
import '../../utils/breakpoint.esm.js';
import '../../utils/context.esm.js';
import '../../utils/dom.esm.js';
import '../../utils/tabbable.esm.js';
import '../../utils/dom-query.esm.js';
import '../../utils/logger.esm.js';
import '../../utils/focus.esm.js';
import { callAllHandlers } from '../../utils/function.esm.js';
import '../../utils/keydown.esm.js';
import '../../utils/lazy.esm.js';
import '../../utils/object.esm.js';
import { mergeRefs } from '../../utils/react-helpers.esm.js';
import '../../utils/style-props.esm.js';
import '../../utils/tailwind.esm.js';
import '../../hooks/use-safe-layout-effects.esm.js';
import '../../hooks/use-callback-ref.esm.js';
import '../../hooks/use-clickable.esm.js';
import '../../hooks/use-clipboard.esm.js';
import '../../hooks/use-combobox-dropdown-style.esm.js';
import '../../hooks/use-combobox-style.esm.js';
import '../../hooks/use-controllable.esm.js';
import '../../hooks/use-local-storage.esm.js';
import '../../hooks/use-media.esm.js';
import '../../hooks/use-prefers-dark-mode.esm.js';
import '../../hooks/use-dark-mode.esm.js';
import '../../hooks/use-force-update.esm.js';
import '../../hooks/use-descendant.esm.js';
import { useIds } from '../../hooks/use-id.esm.js';
import '../../hooks/use-previous.esm.js';
import '../../hooks/use-disclosure.esm.js';
import '../../hooks/use-dropdown-style.esm.js';
import '../../hooks/use-latest-ref.esm.js';
import '../../hooks/use-event-listener.esm.js';
import '../../hooks/use-event-listener-v2.esm.js';
import '../../hooks/use-update-effect.esm.js';
import '../../hooks/use-focus-on-hide.esm.js';
import '../../hooks/use-pointer-event.esm.js';
import '../../hooks/use-focus-on-pointerdown.esm.js';
import '../../hooks/use-focus-on-show.esm.js';
import '../../hooks/use-fork-ref.esm.js';
import '../../hooks/use-hints.esm.js';
import '../../hooks/use-input-style.esm.js';
import '../../hooks/use-key-press.esm.js';
import { useLockBodyScroll } from '../../hooks/use-lock-body-scroll.esm.js';
import '../../hooks/use-platform.esm.js';
import '../../hooks/use-shortcut.esm.js';
import { hideOthers } from 'aria-hidden';
import { useModalManager, manager } from './modal-manager.esm.js';

/**
 * Modal hook to polyfill `aria-modal`.
 *
 * It applies `aria-hidden` to elements behind the modal
 * to indicate that they're `inert`.
 *
 * @param ref React ref of the node
 * @param shouldHide whether `aria-hidden` should be applied
 */

function useAriaHidden(ref, shouldHide) {
  React__default.useEffect(function () {
    if (!ref.current) return;
    var undo = null;

    if (shouldHide && ref.current) {
      undo = hideOthers(ref.current);
    } // eslint-disable-next-line consistent-return


    return function () {
      if (shouldHide) {
        undo == null ? void 0 : undo();
      }
    };
  }, [shouldHide, ref]);
}
/**
 * Modal hook that manages all the logic for the modal dialog widget
 * and returns prop getters, state and actions.
 *
 * @param props
 */

function useModal(props) {
  var _props$open = props.open,
      open = _props$open === void 0 ? false : _props$open,
      onClose = props.onClose,
      id = props.id,
      _props$closeOnOverlay = props.closeOnOverlayClick,
      closeOnOverlayClick = _props$closeOnOverlay === void 0 ? true : _props$closeOnOverlay,
      _props$closeOnEsc = props.closeOnEsc,
      closeOnEsc = _props$closeOnEsc === void 0 ? true : _props$closeOnEsc,
      _props$blockScrollOnM = props.blockScrollOnMount,
      blockScrollOnMount = _props$blockScrollOnM === void 0 ? true : _props$blockScrollOnM,
      _props$useInert = props.useInert,
      useInert = _props$useInert === void 0 ? true : _props$useInert,
      onOverlayClickProp = props.onOverlayClick,
      _props$preserveScroll = props.preserveScrollBarGap,
      preserveScrollBarGap = _props$preserveScroll === void 0 ? true : _props$preserveScroll,
      onEsc = props.onEsc;
  var dialogRef = React__default.useRef(null);
  var overlayRef = React__default.useRef(null);

  var _useIds = useIds(id, 'sajari-modal', 'sajari-modal--header', 'sajari-modal--title', 'sajari-modal--body'),
      contentId = _useIds[0],
      headerId = _useIds[1],
      titleId = _useIds[2],
      bodyId = _useIds[3]; // Hook used to block scrolling once the modal is open


  useLockBodyScroll(dialogRef, open && blockScrollOnMount, preserveScrollBarGap); // Hook used to polyfill `aria-modal` for older browsers.
  // It uses `aria-hidden` to all other nodes.
  // @see https://developer.paciellogroup.com/blog/2018/06/the-current-state-of-modal-dialog-accessibility/

  useAriaHidden(dialogRef, open && useInert); // Hook use to manage multiple or nested modals

  useModalManager(dialogRef, open);
  var mouseDownTarget = React__default.useRef(null);
  var onMouseDown = React__default.useCallback(function (event) {
    mouseDownTarget.current = event.target;
  }, []);
  var onKeyDown = React__default.useCallback(function (event) {
    if (event.key === 'Escape') {
      event.stopPropagation();

      if (closeOnEsc) {
        onClose == null ? void 0 : onClose();
      }

      onEsc == null ? void 0 : onEsc();
    }
  }, [closeOnEsc, onClose, onEsc]);
  var onOverlayClick = React__default.useCallback(function (event) {
    event.stopPropagation(); // Make sure the event starts and ends on the same DOM element.
    //
    // This is used to prevent the modal from closing when you
    // start dragging from the content, and release drag outside the content.
    //
    // We prevent this because it's technically not a considered "click outside"

    if (mouseDownTarget.current !== event.target) return; //  When you click on the overlay, we want to remove only the topmost modal

    if (manager.isTopModal(dialogRef)) {
      if (closeOnOverlayClick) {
        onClose == null ? void 0 : onClose();
      }

      onOverlayClickProp == null ? void 0 : onOverlayClickProp();
    }
  }, [onClose, closeOnOverlayClick, onOverlayClickProp]);

  var _React$useState = React__default.useState(false),
      headerMounted = _React$useState[0],
      setHeaderMounted = _React$useState[1];

  var _React$useState2 = React__default.useState(false),
      bodyMounted = _React$useState2[0],
      setBodyMounted = _React$useState2[1];

  return {
    open: open,
    onClose: onClose,
    headerId: headerId,
    bodyId: bodyId,
    titleId: titleId,
    setBodyMounted: setBodyMounted,
    setHeaderMounted: setHeaderMounted,
    dialogRef: dialogRef,
    getContentProps: function getContentProps(contentProps) {
      if (contentProps === void 0) {
        contentProps = {};
      }

      return _extends({}, contentProps, {
        ref: mergeRefs(contentProps.ref, dialogRef),
        id: contentId,
        role: contentProps.role || 'dialog',
        tabIndex: -1,
        'aria-modal': true,
        'aria-labelledby': headerMounted ? headerId : undefined,
        'aria-describedby': bodyMounted ? bodyId : undefined,
        onClick: callAllHandlers(contentProps.onClick, function (event) {
          return event.stopPropagation();
        })
      });
    },
    getOverlayProps: function getOverlayProps(overlayProps) {
      if (overlayProps === void 0) {
        overlayProps = {};
      }

      return _extends({}, overlayProps, {
        ref: mergeRefs(overlayProps.ref, overlayRef),
        onClick: callAllHandlers(overlayProps.onClick, onOverlayClick),
        onKeyDown: callAllHandlers(overlayProps.onKeyDown, onKeyDown),
        onMouseDown: callAllHandlers(overlayProps.onMouseDown, onMouseDown)
      });
    }
  };
}

export { useAriaHidden, useModal };
//# sourceMappingURL=use-modal.esm.js.map
